import { useAuth } from '../auth/AuthContext';

/**
 * External Django/Keycloak group names. Single source of truth: each must match
 * the backend constant in authentication.constants exactly. Re-exported from the
 * per-role modules for role-specific use.
 */
export const EXTERNAL_WORKFLOW_MANAGER_GROUP = 'External Workflow Manager';
export const EXTERNAL_ROUND_MANAGER_GROUP = 'External Round Manager';

/**
 * True when `pathname` is exactly `prefix` or sits under it as a path segment.
 * Guards against partial-string matches like `/batches-foo` for `/batches`.
 */
export const matchesPathPrefix = (pathname, prefix) => {
    if (typeof pathname !== 'string' || typeof prefix !== 'string') return false;
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
};

/**
 * True when `pathname` matches any of the role's `allowedPathPrefixes`.
 */
export const isPathAllowedForExternalRole = (role, pathname) => {
    if (!role || !Array.isArray(role.allowedPathPrefixes)) return false;
    return role.allowedPathPrefixes.some((p) => matchesPathPrefix(pathname, p));
};

/**
 * Registry of every "external" Django/Keycloak group that should be confined
 * to a narrow slice of the app. Adding a new external role = adding one entry.
 *
 * - groupName: Django/Keycloak group name (must match the backend constant exactly)
 * - landingPath: pathname the user is redirected to after login / when bouncing
 * - allowedPathPrefixes: locations the user may visit; everything else bounces.
 *   Matching is path-segment aware: `/batches` matches `/batches` and
 *   `/batches/123` but NOT `/batches-foo`.
 * - isPanelVisible: predicate used by the sidebar / command palette to decide
 *   whether a panel should appear in nav surfaces
 */
export const EXTERNAL_ROLES = [
    {
        groupName: EXTERNAL_WORKFLOW_MANAGER_GROUP,
        landingPath: '/batches',
        allowedPathPrefixes: ['/batches'],
        isPanelVisible: (panel) => matchesPathPrefix(panel?.uri ?? '', '/batches'),
    },
    {
        // Write-enabled sibling: runs the round lifecycle inside its assigned
        // batches. Same confinement as above -- the difference in capability is
        // carried entirely by Django permissions, not by this registry.
        groupName: EXTERNAL_ROUND_MANAGER_GROUP,
        landingPath: '/batches',
        allowedPathPrefixes: ['/batches'],
        isPanelVisible: (panel) => matchesPathPrefix(panel?.uri ?? '', '/batches'),
    },
];

const extractGroupNames = (user) => {
    if (!user || !Array.isArray(user.groups)) return [];
    return user.groups.map((g) => (typeof g === 'string' ? g : g?.name)).filter(Boolean);
};

/**
 * Returns the active external role profile for the user, or null when the user
 * holds no external role at all.
 *
 * Confinement is driven purely by PRESENCE of an external role: a user in an
 * external group is confined regardless of any other groups they hold. This
 * mirrors the backend (authentication.services.external_workflow_manager
 * .is_external_workflow_manager, which scopes data on group membership alone),
 * so any additive-only group (e.g. Workflow Finance Manager, which just grants
 * the rounds-report export button) can neither lift nor impose a restriction.
 *
 * Internal + external is a misconfiguration on our side, not a supported state,
 * so there is deliberately no "internal group escapes confinement" path.
 *
 * A user who happens to be in multiple external groups picks the first match
 * in EXTERNAL_ROLES order. That edge case is unlikely in practice — keep the
 * registry ordered by preference if it ever matters.
 */
export const getActiveExternalRole = (user) => {
    const groups = extractGroupNames(user);
    return EXTERNAL_ROLES.find((role) => groups.includes(role.groupName)) || null;
};

export const useActiveExternalRole = () => {
    const { user } = useAuth();
    return getActiveExternalRole(user);
};

/**
 * True when the user is in a specific external group AND in no internal group.
 * Use this from individual components when the UI hide is genuinely role-specific
 * (e.g. "EWMs don't see the guideline button"). Prefer useActiveExternalRole()
 * when the behaviour is structural (nav, redirect).
 */
export const useIsInExternalRoleOnly = (groupName) => {
    const role = useActiveExternalRole();
    return role?.groupName === groupName;
};
